import type { LiveMetrics } from "@/types/api";

/**
 * LIVE tab — current-UTC-hour traffic + trailing-24h sparkline.
 * Refreshed every 20s by useVigilSnapshotPoll. Ported from
 * products/x2mail-admin/src/views/LiveView.tsx, adapted to the generated
 * client's camelCase field names (hour_utc -> hourUtc, etc.).
 */
export interface LiveViewProps {
  live: LiveMetrics | null;
  loading: boolean;
}

export function LiveView({ live, loading }: LiveViewProps) {
  if (loading || !live) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-sm text-zinc-500">Ładowanie danych na żywo…</p>
      </div>
    );
  }

  const maxInits = Math.max(...live.hourlySeries24h.map((h) => h.sessionInits), 1);
  const maxGens = Math.max(...live.hourlySeries24h.map((h) => h.generations), 1);
  const maxY = Math.max(maxInits, maxGens, 1);

  return (
    <div className="space-y-6">
      {/* Bieżąca godzina — kafelki główne */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <MetricTile
          label="Sesje (session_inits)"
          value={live.sessionInitsThisHour}
          sub={`godz. ${String(live.hourUtc).padStart(2, "0")}:00 UTC`}
        />
        <MetricTile
          label="Generacje"
          value={live.generationsThisHour}
          sub={`godz. ${String(live.hourUtc).padStart(2, "0")}:00 UTC`}
          highlight={live.sessionInitsThisHour > 0 && live.generationsThisHour === 0}
        />
        <MetricTile
          label="Wizyty Plausible (real-time)"
          value={live.plausibleRealtimeVisitors}
          sub="aktualnie online (przed logowaniem)"
        />
      </div>

      {/* Sparkline 24h */}
      <div className="overflow-x-auto rounded-lg border border-zinc-800 bg-zinc-900 p-4">
        <h3 className="mb-3 text-sm font-semibold text-zinc-300">Ruch ostatnich 24h — sparkline</h3>
        <svg
          viewBox="0 0 480 80"
          width="100%"
          height={80}
          aria-label="Sparkline sesji i generacji za ostatnie 24 godziny"
        >
          {live.hourlySeries24h.map((pt, i) => {
            const x = (i / (live.hourlySeries24h.length - 1)) * 460 + 10;
            const yInits = 72 - (pt.sessionInits / maxY) * 60;
            const yGens = 72 - (pt.generations / maxY) * 60;

            return (
              <g key={pt.hour}>
                <circle cx={x} cy={yInits} r={2} fill="#a65031" opacity={0.8} />
                <circle cx={x} cy={yGens} r={2} fill="#22c55e" opacity={0.8} />
                {i < live.hourlySeries24h.length - 1 &&
                  (() => {
                    const nextPt = live.hourlySeries24h[i + 1];
                    const x2 = ((i + 1) / (live.hourlySeries24h.length - 1)) * 460 + 10;
                    const yInits2 = 72 - (nextPt.sessionInits / maxY) * 60;
                    const yGens2 = 72 - (nextPt.generations / maxY) * 60;
                    return (
                      <>
                        <line
                          x1={x}
                          y1={yInits}
                          x2={x2}
                          y2={yInits2}
                          stroke="#a65031"
                          strokeWidth={1.5}
                          opacity={0.6}
                        />
                        <line
                          x1={x}
                          y1={yGens}
                          x2={x2}
                          y2={yGens2}
                          stroke="#22c55e"
                          strokeWidth={1.5}
                          opacity={0.6}
                        />
                      </>
                    );
                  })()}
              </g>
            );
          })}
          {/* Legend */}
          <circle cx={10} cy={8} r={4} fill="#a65031" />
          <text x={18} y={12} fontSize={9} fill="#71717a">
            session_inits
          </text>
          <circle cx={100} cy={8} r={4} fill="#22c55e" />
          <text x={108} y={12} fontSize={9} fill="#71717a">
            generacje
          </text>
        </svg>
      </div>

      {/* Kraje na żywo */}
      <div>
        <h3 className="mb-3 text-sm font-semibold text-zinc-300">
          Aktywność per kraj — bieżąca godzina
        </h3>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {live.countryLive.map((c) => (
            <div key={c.country} className="rounded-lg border border-zinc-800 bg-zinc-900 p-3">
              <p className="text-base font-semibold text-zinc-100">{c.country}</p>
              <p className="mt-1 text-xs text-zinc-400">
                Sesje: <span className="text-indigo-400">{c.sessionInits}</span>
              </p>
              <p className="text-xs text-zinc-400">
                Generacje:{" "}
                <span
                  className={
                    c.sessionInits > 0 && c.generations === 0
                      ? "text-red-400 font-semibold"
                      : "text-green-400"
                  }
                >
                  {c.generations}
                </span>
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

interface MetricTileProps {
  label: string;
  value: number;
  sub: string;
  highlight?: boolean;
}

function MetricTile({ label, value, sub, highlight = false }: MetricTileProps) {
  return (
    <div
      className={[
        "rounded-lg border p-4",
        highlight ? "border-red-800 bg-red-950/50" : "border-zinc-800 bg-zinc-900",
      ].join(" ")}
    >
      <p className="text-xs text-zinc-500">{label}</p>
      <p
        className={[
          "mt-1 text-3xl font-bold tabular-nums",
          highlight ? "text-red-400" : "text-zinc-100",
        ].join(" ")}
      >
        {value}
      </p>
      <p className="mt-0.5 text-xs text-zinc-600">{sub}</p>
    </div>
  );
}
